import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Box,
  CircularProgress,
} from "@mui/material";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Layout from "./components/Layout";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeContextProvider, useTheme } from "./context/ThemeContext";

// Pages
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import MedicineSearchPage from "./pages/MedicineSearchPage";
import OrderCreatePage from "./pages/OrderCreatePage";
import ReceiptPage from "./pages/ReceiptPage";
import PrescriptionFulfillmentPage from "./pages/PrescriptionFulfillmentPage";
import InventoryPage from "./pages/InventoryPage";
import UserManagementPage from "./pages/UserManagementPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import InventoryManagementPage from "./pages/InventoryManagementPage";
import InventoryAlertsPage from "./pages/InventoryAlertsPage";
import MedicineManagementPage from "./pages/MedicineManagementPage";
import SupplierManagementPage from "./pages/SupplierManagementPage";
import PrescriptionManagementPage from "./pages/PrescriptionManagementPage";
import AuditLogPage from "./pages/AuditLogPage";
import OrderManagementPage from "./pages/OrderManagementPage";
import RoleMenuManagement from "./pages/RoleMenuManagement";
import NotFoundPage from "./pages/NotFoundPage";
import PatientRegistration from "./pages/PatientRegistration";
import DirectivePage from "./pages/DirectivePage";
import PatientManagementPage from "./pages/PatientManagementPage";
import NotificationManagementPage from './pages/NotificationManagementPage';
import NotificationAnalyticsPage from './pages/NotificationAnalyticsPage';
import SalesReportsPage from './pages/SalesReportsPage';
import DrugReferencePage from "./pages/DrugReferencePage";
import LicenseManagementPage from "./pages/LicenseManagementPage";
import SupplierPaymentsPage from "./pages/SupplierPaymentsPage";
import SystemSettingsPage from "./pages/SystemSettingsPage";
import BranchManagementPage from "./pages/BranchManagementPage";

// Enhanced theme with better customization
const getDesignTokens = (mode) => ({
  palette: {
    mode,
    primary: { main: "#1976d2", light: "#42a5f5", dark: "#1565c0", contrastText: "#ffffff" },
    secondary: { main: "#dc004e", light: "#ff5983", dark: "#9a0036", contrastText: "#ffffff" },
    background: { default: mode === 'light' ? '#f8fafc' : '#0a1929', paper: mode === 'light' ? '#ffffff' : '#132f4c' },
    text: { primary: mode === 'light' ? '#1a2027' : '#ffffff', secondary: mode === 'light' ? '#656f7d' : '#b0bec5' },
    divider: mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)',
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        InputLabelProps: { shrink: true },
      },
      styleOverrides: {
        root: {
          '& label': {
            fontWeight: 600,
            fontSize: '1rem',
            color: mode === 'light' ? '#1976d2' : '#90caf9',
          },
          '& label.Mui-focused': {
            color: mode === 'light' ? '#115293' : '#ffffff',
          },
          '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            backgroundColor: mode === 'light' ? 'rgba(25, 118, 210, 0.02)' : 'rgba(255, 255, 255, 0.02)',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: mode === 'light' ? 'rgba(25, 118, 210, 0.04)' : 'rgba(255, 255, 255, 0.04)',
            },
          },
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          borderRadius: '12px',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '24px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '10px',
          textTransform: 'none',
          fontWeight: 600,
          padding: '8px 20px',
        },
      },
    },
  },
});

function RouteTitleUpdater() {
  const location = useLocation();

  React.useEffect(() => {
    let pathSegments = location.pathname.split("/").filter(Boolean);
    let name = "Dashboard";

    if (pathSegments.length > 0) {
      // Use the last segment or a specific logic, let's just capitalize the last segment
      let segment = pathSegments[pathSegments.length - 1];
      if (['manage', 'alerts', 'create'].includes(segment) && pathSegments.length > 1) {
        segment = pathSegments[pathSegments.length - 2] + " " + segment;
      }
      name = segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, " ");
    }

    document.title = `Pharmacy System | ${name}`;
  }, [location.pathname]);

  return null;
}

function App() {
  return (
    <ThemeContextProvider>
      <ThemedApp />
    </ThemeContextProvider>
  );
}

function ThemedApp() {
  const { mode } = useTheme();
  const theme = React.useMemo(() => createTheme(getDesignTokens(mode)), [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastContainer position="top-right" autoClose={4000} hideProgressBar={false} newestOnTop closeOnClick pauseOnFocusLoss draggable pauseOnHover theme={mode} style={{ zIndex: 9999 }} />
      <AuthProvider>
        <Router basename="/">
          <RouteTitleUpdater />
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<LoginPage />} />

            {/* Protected Routes with Layout */}
            <Route path="/" element={
              <PrivateRoute allowedRoles={["admin", "pharmacist", "cashier", "doctor", "superadmin"]}>
                <Layout />
              </PrivateRoute>
            }>
              <Route index element={<Navigate to="/dashboard" />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="medicines" element={<MedicineSearchPage />} />

              {/* Billing & Orders */}
              <Route path="orders/create" element={<OrderCreatePage />} />
              <Route path="orders/manage" element={
                <PrivateRoute allowedRoles={["admin", "pharmacist", "superadmin"]}>
                  <OrderManagementPage />
                </PrivateRoute>
              } />

              <Route path="receipt/:orderId" element={<ReceiptPage />} />

              {/* Medicines & Inventory */}
              <Route path="inventory" element={<InventoryPage />} />
              <Route path="inventory/manage" element={<InventoryManagementPage />} />
              <Route path="inventory/alerts" element={<InventoryAlertsPage />} />
              <Route path="medicines/manage" element={<MedicineManagementPage />} />
              <Route path="suppliers" element={<SupplierManagementPage />} />
              <Route path="suppliers/payments" element={
                <PrivateRoute allowedRoles={["admin", "superadmin", "pharmacist"]}>
                  <SupplierPaymentsPage />
                </PrivateRoute>
              } />

              {/* Prescriptions */}
              <Route path="prescriptions" element={<PrescriptionFulfillmentPage />} />
              <Route path="prescriptions/manage" element={<PrescriptionManagementPage />} />

              {/* Patients */}
              <Route path="patients/register" element={
                <PrivateRoute allowedRoles={["pharmacist", "admin", "superadmin"]}>
                  <PatientRegistration />
                </PrivateRoute>
              } />

              <Route path="patients/manage" element={
                <PrivateRoute allowedRoles={["admin", "pharmacist", "superadmin"]}>
                  <PatientManagementPage />
                </PrivateRoute>
              } />

              {/* Notifications */}
              <Route path="notifications" element={
                <PrivateRoute allowedRoles={["admin", "pharmacist", "superadmin"]}>
                  <NotificationManagementPage />
                </PrivateRoute>
              } />
              <Route path="notifications/analytics" element={
                <PrivateRoute allowedRoles={["admin", "pharmacist", "superadmin"]}>
                  <NotificationAnalyticsPage />
                </PrivateRoute>
              } />

              <Route path="reports/sales" element={
                <PrivateRoute allowedRoles={["admin", "superadmin", "pharmacist"]}>
                  <SalesReportsPage />
                </PrivateRoute>
              } />

              <Route path="drug-reference" element={
                <PrivateRoute allowedRoles={["admin", "pharmacist", "superadmin", "assistant", "doctor"]}>
                  <DrugReferencePage />
                </PrivateRoute>
              } />
              {/* Directives */}
              <Route path="directives" element={
                <PrivateRoute allowedRoles={["admin", "superadmin", "pharmacist"]}>
                  <DirectivePage />
                </PrivateRoute>
              } />

              {/* Admin only */}
              <Route path="users" element={
                <PrivateRoute allowedRoles={["admin", "superadmin"]}>
                  <UserManagementPage />
                </PrivateRoute>
              } />

              <Route path="change-password" element={<ChangePasswordPage />} />

              <Route path="audit-logs" element={
                <PrivateRoute allowedRoles={["admin", "superadmin"]}>
                  <AuditLogPage />
                </PrivateRoute>
              } />

              <Route path="license" element={
                <PrivateRoute allowedRoles={["admin", "superadmin"]}>
                  <LicenseManagementPage />
                </PrivateRoute>
              } />

              {/* Superadmin Panel */}
              <Route path="superadmin" element={
                <PrivateRoute allowedRoles={["superadmin"]}>
                  <RoleMenuManagement />
                </PrivateRoute>
              } />

              <Route path="settings" element={
                <PrivateRoute allowedRoles={["superadmin"]}>
                  <SystemSettingsPage />
                </PrivateRoute>
              } />

              <Route path="branches" element={
                <PrivateRoute allowedRoles={["superadmin"]}>
                  <BranchManagementPage />
                </PrivateRoute>
              } />

              {/* 404 Fallback */}
              <Route path="*" element={<NotFoundPage />} />
            </Route>

            {/* Catch all - redirect to dashboard if authenticated, otherwise to login */}
            <Route path="*" element={<NavigateToAppropriatePage />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

// Component to redirect to appropriate page
function NavigateToAppropriatePage() {
  const { user } = useAuth();
  return <Navigate to={user ? "/dashboard" : "/login"} replace />;
}

// PrivateRoute component
function PrivateRoute({ children, allowedRoles = [] }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        bgcolor: 'background.default'
      }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!user) {
    // Store the attempted URL for redirecting after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    // User doesn't have required role, redirect to dashboard
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

export default App;